import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import CargomaticInput from 'components/v2/input';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import CustomerSearch from 'components/site/customer-search-alt';
import CargomaticRadio from 'components/v2/forms/Radio';
import CargomaticSelect from 'components/v2/select';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import formatMoney from 'utils/formatters/Money';
import { validDecimal } from 'utils/accounting-util';
import ViewAccessorialRequestDocumentRequirements from 'components/modules/shipments/accessorials/ViewAccessorialDocumentRequirements';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import DateIconButton from '../../../v2/buttons/DateIconButton';
import Label from '../../../v2/forms/Label';
import { sluggify } from 'utils/localization/utils';

function getMomentDate(date) {
  if (!date) {
    return null;
  }

  return moment(date);
}

class DrayageAccessorialRow extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedAccessorial: null,
      errors: {}, // Not directly used yet but allows flexibility for the future
    };
  }

  static propTypes = {
    shipment: PropTypes.shape({
      _id: PropTypes.string.isRequired,
      shipment_id: PropTypes.string.isRequired,
      equipments: PropTypes.arrayOf(
        PropTypes.shape({
          equipment_id: PropTypes.string.isRequired,
          _id: PropTypes.string.isRequired,
        }),
      ),
      meta: PropTypes.shape({
        name: PropTypes.string,
        type: PropTypes.string,
        charge_code: PropTypes.string,
        equipment: PropTypes.string,
        shipper_cost: PropTypes.string,
        carrier_cost: PropTypes.string,
        errors: PropTypes.object,
      }),
    }).isRequired,
    accessorials: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        _id: PropTypes.string,
        slug: PropTypes.string,
      }),
    ),
    onFieldChange: PropTypes.func.isRequired,
    onApplyAll: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    handleUpdateAccessorialRequiredDocs: PropTypes.func.isRequired,
    edit: PropTypes.bool,
    handleUpdateCustomerApproval: PropTypes.func,
  };

  static defaultProps = {
    accessorials: [],
    edit: false,
  };

  componentDidMount() {
    const { edit, shipment, accessorials, defaultAccessorialType = null } = this.props;
    if (edit && shipment?.meta?.type) {
      const selectedAccessorial = accessorials.find(
        (a) => a.slug === shipment?.meta?.type,
      );
      this.setState({ selectedAccessorial });
    }
    if (!edit && defaultAccessorialType) {
      this.handleTypeChange(defaultAccessorialType);
    }
  }

  componentDidUpdate(prevProps) {
    const { edit, shipment, accessorials } = this.props;
    if (edit && prevProps?.shipment?.meta?.type !== shipment?.meta?.type) {
      const selectedAccessorial = accessorials.find(
        (a) => a.slug === shipment?.meta?.type,
      );
      this.setState({ selectedAccessorial });
    }
  }

  getJobId() {
    const {
      shipment: { _id },
      index,
    } = this.props;
    return `${_id}-${index}`;
  }

  getKey(prefix) {
    return `${prefix}-${this.getJobId()}`;
  }

  getShipmentId() {
    const {
      shipment: { shipment_id },
    } = this.props;

    return shipment_id;
  }

  getShipmentLink() {
    const {
      shipment: { _id },
    } = this.props;
    return `/shipments/${_id}`;
  }

  getMeta() {
    const {
      shipment: { meta },
    } = this.props;
    return meta || {};
  }

  getEquipments() {
    return this.props?.shipment?.equipments || [];
  }

  getValidationClass(field) {
    const {
      shipment: {
        meta: { errors },
      },
    } = this.props;

    if (errors[field]) {
      return {
        error: true,
      };
    }
    return {
      error: false,
    };
  }

  handleApplyAll = () => this.props.onApplyAll();

  handleRemove = () => this.props.onRemove();

  invalidUnitQty = (field, value) =>
    field.indexOf('unit_qty') > -1 && !validDecimal(value);

  handleFieldChange = (field) => (value) => {
    // check whether it's a unit quantity field or not
    if (this.invalidUnitQty(field, value)) {
      // if it's a unit field but the input is invalid, skip
      return;
    }
    this.props.onFieldChange(field, value);
  };

  handleVendorSelect = (_value, item) => {
    this.props.onVendorFieldChange({
      vendor_name: item.company_name,
      vendor: item._id,
    });
  };

  handleVendorFieldChange = (value) => {
    this.props.onVendorFieldChange({
      vendor_name: value,
      vendor: null,
    });
  };

  handleEquipmentChange = (e) => {
    this.props.onFieldChange('equipment', e.target.value);
  };

  handleTypeChange = (type) => {
    const { edit, accessorials, onFieldChange } = this.props;
    const selectedAccessorial = accessorials.find((a) => a.slug === type);
    onFieldChange('type', type);
    onFieldChange('charge_code', selectedAccessorial.charge_code);
    if (type && type !== 'other') {
      const name = accessorials.find((a) => a.slug === type).name;

      this.resetRequiredFieldsMeta(selectedAccessorial);
      this.setState(
        {
          selectedAccessorial,
        },
        () => onFieldChange('name', name),
      );
    } else {
      this.resetRequiredFieldsMeta(null);
      this.setState(
        {
          selectedAccessorial: null,
        },
        () => onFieldChange('name', ''),
      );
    }
  };

  handleCustomerApprovalChanges = (required, provided) => {
    this.props.handleUpdateCustomerApproval(required, provided);
  };

  renderShipmentLink() {
    const key = this.getKey('shipment-link');
    const shipmentLink = this.getShipmentLink();
    return (
      <td key={key}>
        <div className="mt-2" style={{ minWidth: '70px' }}>
          <a href={shipmentLink} target="_blank" rel="noreferrer noopener">
            {this.getShipmentId()}
          </a>
        </div>
      </td>
    );
  }

  handleLocationChange = (item, field) => {
    const { value } = item;
    this.props.onFieldChange(field, value);
  };

  renderEquipmentDropdown() {
    const { t, edit } = this.props;
    const key = this.getKey('equipments');
    const equipments = this.getEquipments();
    const meta = this.getMeta();
    const className = classnames({
      ...this.getValidationClass('equipment'),
    });

    return (
      <td key={key}>
        <div style={{ minWidth: '156px' }} className={className}>
          <select
            className="form-select"
            value={meta.equipment}
            onChange={this.handleEquipmentChange}
            disabled={edit}
          >
            <option>{t('SELECT_EQUIPMENTS')}</option>
            {equipments.map((equipment) => (
              <option key={equipment._id} value={equipment._id}>
                {equipment.equipment_id}
              </option>
            ))}
          </select>
        </div>
        {!edit && this.renderLocation('location', false)}
      </td>
    );
  }

  renderLocation(field = 'location', disabled = false) {
    const { t, edit } = this.props;
    const key = this.getKey('equipments');
    const meta = this.getMeta();
    const className = classnames({
      'mb-4': edit,
    });

    const { errors } = this.state;
    const { required_fields } = meta;
    const locationTypes = [
      {
        localizationSlug: 'CUSTOMER',
        value: 'customer',
      },
      {
        localizationSlug: 'TERMINAL',
        value: 'terminal',
      },
    ];
    if ((required_fields || []).includes('location_required')) {
      return (
        <div className={className} style={{ marginTop: '5px' }}>
          <CargomaticSelect
            selected={meta[field]?.toLowerCase()}
            fullWidth={false}
            items={locationTypes}
            valueKey="value"
            textKey="localizationSlug"
            error={errors[field]} // Not directly used yet but allows flexibility for the future
            defaultSelectLabel=""
            label={titleize(t('LOCATION'))}
            info={t('LOCATION_CUSTOMER_OR_TERMINAL')}
            onSelect={(item) => this.handleLocationChange(item, field)}
            showCaption
            disabled={disabled}
          />
        </div>
      );
    }
  }

  handleProcessingDocumentUpload = (uploading) => {
    if (uploading) console.log('accessorial document uploading');
    if (!uploading) console.log('accessorial document uploaded successfully');
  };

  renderRequiredAccessorialDocuments() {
    if (!this.state.selectedAccessorial) {
      return null;
    }
    return (
      <ViewAccessorialRequestDocumentRequirements
        containerClassName="mb-4 p-2"
        accessorial={this.state.selectedAccessorial}
        shipper={
          this.props.shipment?.shipper?._id || this.props.shipment?.shipper
        }
        shipment={this.props.shipment}
        handleCustomerApprovalChanges={this.handleCustomerApprovalChanges}
        handleUpdateAccessorialRequiredDocs={
          this.props.handleUpdateAccessorialRequiredDocs
        }
      />
    );
  }

  renderTypeDropdown() {
    const { t, edit } = this.props;
    const meta = this.getMeta();
    const className = classnames({
      ...this.getValidationClass('type'),
    });
    return (
      <div style={{ minWidth: '180px' }} className={className}>
        <select
          className="mb-2 form-select"
          value={meta.type}
          onChange={(e) => this.handleTypeChange(e.target.value)}
          disabled={edit}
        >
          <option value="">{t('SELECT_ACCESSORIAL_TYPE')}</option>
          {this.props.accessorials
            .filter((acc) => acc.slug !== 'other')
            .map((a) => (
              <option key={a.slug} value={a.slug}>
                {t((a.slug || '').toUpperCase())}
              </option>
            ))}
        </select>
      </div>
    );
  }

  renderNameInput() {
    const meta = this.getMeta();
    const { type, name } = meta;
    const { t } = this.props;

    if (type !== 'other') {
      return null;
    }

    const className = classnames({
      ...this.getValidationClass('name'),
    });
    return (
      <div style={{ minWidth: '120px' }} className={className}>
        <CargomaticInput
          className="mb-4"
          hideLabel
          maxLength={200}
          type="text"
          placeholder={t('ENTER_DESCRIPTION')}
          value={name}
          onChange={this.handleFieldChange('name')}
        />
      </div>
    );
  }

  renderName() {
    const { edit } = this.props;
    const key = this.getKey('name');
    const meta = this.getMeta();
    const { description, required_fields } = meta;
    const { t } = this.props;
    return (
      <td key={key}>
        <div>
          {this.renderTypeDropdown()}
          {this.renderNameInput()}
        </div>
        {!edit && this.renderDescription('description')}
      </td>
    );
  }

  renderDescription(field = 'description', disabled = false) {
    const { edit } = this.props;
    const meta = this.getMeta();
    const { required_fields } = meta;
    const { t } = this.props;
    const descriptionClass = classnames({
      'mb-4': edit,
    });
    if ((required_fields || []).includes('description_required')) {
      return (
        <div className={descriptionClass}>
          <label htmlFor="description" className="form-label">
            {titleize(t('DESCRIPTION', { count: 1 }))}
          </label>
          <CargomaticInput
            hideLabel
            maxLength={300}
            className="w-full"
            type="text"
            placeholder={titleize(t('DESCRIPTION', { count: 1 }))}
            onChange={this.handleFieldChange(field)}
            value={meta[field]}
            disabled={disabled}
          />
        </div>
      );
    }
  }

  renderVendor() {
    const meta = this.getMeta();
    const { payee_type, vendor_name } = meta;
    const key = this.getKey('vendor');
    const { carrierEnabled, t, edit } = this.props;

    const jobId = this.getJobId();
    const carrierId = `carrier-${jobId}`;
    const vendorId = `vendor-${jobId}`;
    const name = `payee_type-${jobId}`;
    return (
      <td className={key}>
        <div className="">
          <CargomaticRadio
            id={carrierId}
            name={name}
            disabled={edit || !carrierEnabled}
            className="mb-2"
            value="carrier"
            checked={payee_type === 'carrier'}
            onChange={this.handleFieldChange('payee_type')}
          >
            <Label
              helptext={
                !this.props.carrierEnabled ? t('CARRIER_ACCESSORIAL_ERROR') : ''
              }
            >
              {titleize(t('CARRIER', { count: 1 }))}
            </Label>
          </CargomaticRadio>
          <CargomaticRadio
            id={vendorId}
            name={name}
            value="vendor"
            checked={payee_type === 'vendor'}
            onChange={this.handleFieldChange('payee_type')}
            disabled={edit}
          >
            {titleize(t('VENDOR', { count: 1 }))}
          </CargomaticRadio>
        </div>
        {payee_type === 'vendor' && (
          <div className="mt-4">
            <label className="mb-1 form-label">{t('SEARCH_VENDORS')}</label>
            <CustomerSearch
              clearOnSelect={false}
              onChange={this.handleVendorFieldChange}
              onSelect={this.handleVendorSelect}
              placeholder={t('SEARCH_FOR_VENDOR')}
              type="vendors"
              authorizedOnly={false}
              value={vendor_name}
              disabled={edit}
            />
          </div>
        )}
      </td>
    );
  }

  renderDate(id /* accessorial */, disabled = false) {
    const { t, edit } = this.props;
    const meta = this.getMeta();
    const { begin_datetime, end_datetime, required_fields } = meta;
    const dateClass = classnames({
      'md:w': true,
      'mb-4': edit,
    });
    const DATE_FORMAT = 'MM-DD-YYYY';
    return (
      <div className={dateClass} key={id}>
        {(id === 'begin_datetime' || id === 'shipper_begin_datetime') &&
          (required_fields || []).includes('begin_date_required') && (
            <div className="px-2">
              <label htmlFor="startDate" className="form-label">
                {t('BEGIN_DATE')}
              </label>
              <DatePicker
                id="beginDate"
                selected={getMomentDate(meta[id])}
                onChange={this.handleFieldChange(id)}
                customInput={<DateIconButton />}
                dateFormat={DATE_FORMAT}
                shouldCloseOnSelect
                showTimeSelect
                timeIntervals={5}
                disabled={disabled}
              />
            </div>
          )}
        {(id === 'end_datetime' || id === 'shipper_end_datetime') &&
          (required_fields || []).includes('end_date_required') && (
            <div className="px-2">
              <label htmlFor="endDate" className="form-label">
                {t('END_DATE')}
              </label>
              <DatePicker
                id="endDate"
                selected={getMomentDate(meta[id])}
                onChange={this.handleFieldChange(id)}
                customInput={<DateIconButton />}
                dateFormat={DATE_FORMAT}
                shouldCloseOnSelect
                showTimeSelect
                timeIntervals={5}
                disabled={disabled}
              />
            </div>
          )}
      </div>
    );
  }

  renderShipperCost() {
    const { t, edit } = this.props;
    const meta = this.getMeta();
    const { shipper_cost, disable_payer, ratesRequestPending } = meta;
    const key = this.getKey('shipper_cost');
    const className = classnames({
      ...this.getValidationClass('shipper_cost'),
    });
    return (
      <td key={key}>
        <div style={{ minWidth: '126px' }} className={className}>
          <CargomaticInput
            className="mb-4"
            hideLabel
            maxLength={6}
            type="number"
            placeholder={titleize(t('SHIPPER_RATE'))}
            value={shipper_cost}
            disabled={ratesRequestPending || disable_payer}
            onChange={this.handleFieldChange('shipper_cost')}
          />
        </div>
        <div className="flex justify-between" key="date-range">
          {!edit && this.renderDate('begin_datetime')}
          {edit && this.renderDate('shipper_begin_datetime', disable_payer)}
        </div>
        <div className="flex justify-between" key="date-range">
          {edit && this.renderLocation('shipper_location', disable_payer)}
        </div>
        <div className="flex justify-between" key="date-range">
          {edit && this.renderNotesInput('shipper_note', disable_payer)}
        </div>
      </td>
    );
  }

  renderCarrierCost() {
    const { t, edit } = this.props;
    const meta = this.getMeta();
    const { carrier_cost, disable_payee, ratesRequestPending, isVendorPaid = false } = meta;
    const key = this.getKey('carrier_cost');
    const className = classnames({
      ...this.getValidationClass('carrier_cost'),
    });
    return (
      <td key={key}>
        <div style={{ minWidth: '126px' }} className={className}>
          <CargomaticInput
            className="mb-4"
            hideLabel
            maxLength={6}
            type="number"
            placeholder={titleize(t('CARRIER_COST'))}
            value={carrier_cost}
            onChange={this.handleFieldChange('carrier_cost')}
            disabled={ratesRequestPending || disable_payee || isVendorPaid}
          />
        </div>
        <div className="flex justify-between" key="date-range">
          {edit && this.renderDate('begin_datetime', disable_payee || isVendorPaid)}
        </div>
        <div className="flex justify-between" key="date-range">
          {edit && this.renderLocation('location', disable_payee || isVendorPaid)}
        </div>
        <div className="flex justify-between" key="date-range">
          {edit && this.renderNotesInput('carrier_note', disable_payee || isVendorPaid)}
        </div>
      </td>
    );
  }

  renderUnits(type, disabled = false) {
    const { t, edit } = this.props;
    const meta = this.getMeta();
    const key = this.getKey(`${type}_unit_qty`);
    const className = classnames({
      ...this.getValidationClass(type),
    });

    return (
      <td key={key}>
        <div style={{ minWidth: '200px' }} className={className}>
          <CargomaticInput
            className="mb-4"
            hideLabel
            type="number"
            min="0"
            step="0.5"
            value={meta[`${type}_unit_qty`]}
            onChange={this.handleFieldChange(`${type}_unit_qty`)}
            disabled={disabled}
          />
        </div>
        {type === 'shipper' && (
          <>
            <div className="flex justify-between" key="date-range">
              {!edit && this.renderDate('end_datetime')}
              {edit && this.renderDate('shipper_end_datetime', disabled)}
            </div>
            <div className="flex justify-between" key="date-range">
              {edit && this.renderDescription('shipper_description', disabled)}
            </div>
          </>
        )}
        {type === 'carrier' && (
          <>
            <div className="flex justify-between" key="date-range">
              {edit && this.renderDate('end_datetime', disabled)}
            </div>
            <div className="flex justify-between" key="date-range">
              {edit && this.renderDescription('description', disabled)}
            </div>
          </>
        )}
      </td>
    );
  }

  renderTotal(type) {
    const meta = this.getMeta();
    const key = this.getKey(`${type}_total`);
    const rate = parseFloat(meta[`${type}_cost`]);
    const units = parseFloat(meta[`${type}_unit_qty`]);

    const total = (rate || 0) * (units || 1);

    return (
      <td key={key}>
        <div>
          <p
            className="mt-2"
            style={{ minWidth: '75px', maxWidth: '75px', height: '30px' }}
          >
            {formatMoney(total || 0, this.props.shipment?.currency)}
          </p>
        </div>
      </td>
    );
  }

  renderNotesInput(input_type = 'notes', disabled = false) {
    const { t, edit } = this.props;
    const meta = this.getMeta();
    const key = this.getKey(input_type);
    const className = classnames({
      ...this.getValidationClass('notes'),
      'mb-4': edit,
    });
    return (
      <td key={key}>
        <div style={{ minWidth: '240px' }} className={className}>
          {edit && (
            <label htmlFor="description" className="form-label">
              {titleize(t(sluggify(input_type), { count: 1 }))}
            </label>
          )}
          <CargomaticInput
            className="mb-4"
            hideLabel
            maxLength={300}
            type="text"
            placeholder={titleize(t('NOTE', { count: 2 }))}
            onChange={this.handleFieldChange(input_type)}
            value={meta[input_type]}
            disabled={disabled}
          />
        </div>
      </td>
    );
  }

  renderApplyAll() {
    const key = this.getKey('apply-all');
    const { t, disabledApplyBtn } = this.props;
    return (
      <td key={key}>
        <button
          type="button"
          style={{ minWidth: '80px' }}
          className="button button-hollow-green"
          disabled={disabledApplyBtn}
          onClick={this.handleApplyAll}
        >
          {titleize(t('APPLY'))}
        </button>
      </td>
    );
  }

  resetRequiredFieldsMeta(selectedAccessorial) {
    this.props.onFieldChange('location', null);
    this.props.onFieldChange('begin_datetime', null);
    this.props.onFieldChange('end_datetime', null);
    this.props.onFieldChange('description', null);
    if (!selectedAccessorial) {
      this.props.onFieldChange('required_fields', null);
    } else {
      const required_fields = [];
      if (selectedAccessorial.begin_date_required) {
        required_fields.push('begin_date_required');
      }
      if (selectedAccessorial.end_date_required) {
        required_fields.push('end_date_required');
      }
      if (selectedAccessorial.location_required) {
        required_fields.push('location_required');
      }
      if (selectedAccessorial.description_required) {
        required_fields.push('description_required');
      }
      this.props.onFieldChange('required_fields', required_fields);
    }
  }

  renderActions() {
    const { edit } = this.props;
    const key = this.getKey('remove');
    const id = this.getJobId();

    return (
      <td key={key}>
        <div className="flex justify-center mt-2">
          <button type="button" onClick={this.handleRemove}>
            <i className="fa fa-close" />
          </button>
          {!edit && (
            <button
              datatestId="drayageAccessorialRowOnRowAddButton"
              className="ml-2"
              type="button"
              onClick={() => this.props.onRowAdd(id)}
            >
              <i
                style={{ fontSize: '12px' }}
                className="glyphicon glyphicon-plus text-green"
              />
            </button>
          )}
        </div>
      </td>
    );
  }

  render() {
    const { edit } = this.props;
    const { disable_payer, disable_payee, isVendorPaid } = this.getMeta();
    return (
      <>
        <tr className="w-full">
          {this.renderShipmentLink()}
          {this.renderEquipmentDropdown()}
          {this.renderName()}
          {this.renderVendor()}
          {this.renderShipperCost()}
          {this.renderUnits('shipper', disable_payer)}
          {this.renderTotal('shipper')}
          {this.renderCarrierCost()}
          {this.renderUnits('carrier', disable_payee || isVendorPaid)}
          {this.renderTotal('carrier')}
          {!edit && this.renderNotesInput()}
          {!edit && this.renderApplyAll()}
          {this.renderActions()}
        </tr>
        {featureEnabled(CONFIGURED_FEATURES.SSR) && (
          <tr className="w-full">
            {this.renderRequiredAccessorialDocuments()}
          </tr>
        )}
      </>
    );
  }
}

export default withTranslation()(DrayageAccessorialRow);
